import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PricelistFull, PricelistProduct, PricelistUpdate } from '@smile/shared';
import { PRICELIST_DAYS, PRICELIST_PRODUCT_LABEL, PRICELIST_PRODUCTS } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { can } from '../lib/perms';

/**
 * Nastavení → Ceníky — the legacy pricelist table as two screens: the list
 * of lists, and one list's editor (title, promo codes, the dynamic flag with
 * its two capacities, the 3 × 32 grid of day prices, the validity windows).
 * The web site charges from these rows, so a change here is a change for
 * the customer the moment it is saved.
 */
function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}

function czDay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(d)}. ${Number(m)}. ${y}`;
}

export function Ceniky() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/settings/*', 'write');
  const q = useQuery({ queryKey: ['settings', 'pricelists'], queryFn: () => api.pricelistsFull() });
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [copyFrom, setCopyFrom] = useState('58');
  const [showRetired, setShowRetired] = useState(false);
  const create = useMutation({
    mutationFn: () => api.createPricelist({ title: title.trim(), copyFromId: copyFrom ? Number(copyFrom) : null }),
    onSuccess: (p) => navigate(`/nastaveni/ceniky/${p.id}`),
  });
  const rows = (q.data ?? []).filter((p) => showRetired || p.status === 'ok');

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Ceníky</h1>
        <span className="sub">{q.data ? `${q.data.filter((p) => p.status === 'ok').length} živých · ${q.data.filter((p) => p.status !== 'ok').length} vyřazených` : '…'}</span>
        <div className="acts">
          <Link to="/nastaveni" className="btn sm">
            ← Nastavení
          </Link>
        </div>
      </div>
      <div className="card toolbar">
        <label className="chk">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Zobrazit i vyřazené
        </label>
        <span className="was" style={{ marginLeft: 'auto' }}>
          Web účtuje z těchto ceníků; přepážka počítá stejně. Změna platí okamžitě po uložení.
        </span>
      </div>
      <div className="card tablecard" style={{ marginTop: 14 }}>
        {q.isPending ? (
          <div className="state">Načítám…</div>
        ) : q.isError ? (
          <div className="state" role="alert">
            Ceníky se nepodařilo načíst.
          </div>
        ) : (
          <div className="tablewrap sestavawrap">
            <table className="ceniky">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Název</th>
                  <th>Promo kódy</th>
                  <th>Dynamický</th>
                  <th className="num">Kap. venku</th>
                  <th className="num">Kap. garáž</th>
                  <th>Platnost</th>
                  <th className="num">1 den / 7 dní (Venku)</th>
                  <th className="num">Rezervací</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className={p.status !== 'ok' ? 'deleted' : undefined}>
                    <td className="num was">{p.id}</td>
                    <td>
                      <b>
                        <Link to={`/nastaveni/ceniky/${p.id}`}>{p.title}</Link>
                      </b>
                      {p.id === 58 && <span className="stav gray" style={{ marginLeft: 6 }}>základní</span>}
                    </td>
                    <td>
                      {p.promo.split(/[\s,]+/).filter(Boolean).map((c) => (
                        <code key={c} className="promo">
                          {c}
                        </code>
                      ))}
                    </td>
                    <td>{p.dynamic ? 'ano' : <span className="was">ne</span>}</td>
                    <td className="num">{p.capacityOutside ?? <span className="was">—</span>}</td>
                    <td className="num">{p.capacityGarage ?? <span className="was">—</span>}</td>
                    <td className="was">{p.periods.length === 0 ? 'vždy' : p.periods.map((w) => `${czDay(w.from)}–${czDay(w.till)}`).join(', ')}</td>
                    <td className="num">
                      {CZK.format(p.columns.cheap[0] || p.columns.outside[0])} / {CZK.format(p.columns.cheap[6] || p.columns.outside[6])} Kč
                    </td>
                    <td className="num">{p.reservations ? CZK.format(p.reservations) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {canWrite && (
        <form
          className="card detailcard"
          style={{ marginTop: 14 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim() && !create.isPending) create.mutate();
          }}
        >
          <h2>Nový ceník</h2>
          <div className="efgrid">
            <label className="ef wide2">
              Název
              <input value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} placeholder="např. Léto 2027" required />
            </label>
            <label className="ef">
              Zkopírovat ceny z
              <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                <option value="">prázdný (samé nuly)</option>
                {(q.data ?? [])
                  .filter((p) => p.status === 'ok')
                  .map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      #{p.id} {p.title}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <div className="efactions">
            <button type="submit" className="btn primary" disabled={!title.trim() || create.isPending}>
              {create.isPending ? 'Zakládám…' : 'Založit ceník'}
            </button>
            {errText(create.error) && (
              <span className="arrive-error" role="alert">
                {errText(create.error)}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

type Draft = {
  title: string;
  note: string;
  promo: string;
  dynamic: boolean;
  capacityOutside: string;
  capacityGarage: string;
  status: 'ok' | 'deleted';
  columns: Record<PricelistProduct, string[]>;
};

function toDraft(p: PricelistFull): Draft {
  return {
    title: p.title,
    note: p.note,
    promo: p.promo,
    dynamic: p.dynamic,
    capacityOutside: p.capacityOutside == null ? '' : String(p.capacityOutside),
    capacityGarage: p.capacityGarage == null ? '' : String(p.capacityGarage),
    status: p.status,
    columns: { cheap: p.columns.cheap.map(String), outside: p.columns.outside.map(String), inside: p.columns.inside.map(String) },
  };
}

function diff(p: PricelistFull, d: Draft): PricelistUpdate | null {
  const out: PricelistUpdate = {};
  if (d.title.trim() !== p.title) out.title = d.title.trim();
  if (d.note.trim() !== p.note) out.note = d.note.trim();
  if (d.promo.trim() !== p.promo) out.promo = d.promo.trim();
  if (d.dynamic !== p.dynamic) out.dynamic = d.dynamic;
  if (d.status !== p.status) out.status = d.status;
  const cap = (v: string): number | null | false => {
    if (v.trim() === '') return null;
    const x = Number(v);
    return Number.isInteger(x) && x >= 0 ? x : false;
  };
  const co = cap(d.capacityOutside);
  const cg = cap(d.capacityGarage);
  if (co === false || cg === false) return null;
  if (co !== p.capacityOutside) out.capacityOutside = co;
  if (cg !== p.capacityGarage) out.capacityGarage = cg;
  const columns: NonNullable<PricelistUpdate['columns']> = {};
  for (const prod of PRICELIST_PRODUCTS) {
    const nums = d.columns[prod].map((v) => Number(v));
    if (nums.some((x) => !Number.isInteger(x) || x < 0 || x > 65_535)) return null;
    if (nums.some((x, i) => x !== p.columns[prod][i])) columns[prod] = nums;
  }
  if (Object.keys(columns).length) out.columns = columns;
  if (out.title === '') return null;
  return Object.keys(out).length ? out : null;
}

export function CenikDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/settings/*', 'write');
  const q = useQuery({ queryKey: ['settings', 'pricelists', id], queryFn: () => api.pricelistFull(id), enabled: Number.isInteger(id) && id > 0 });
  const [d, setD] = useState<Draft | null>(null);
  useEffect(() => {
    if (q.data) setD(toDraft(q.data));
  }, [q.data]);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['settings', 'pricelists'] });
    await qc.invalidateQueries({ queryKey: ['catalog', 'pricelists'] });
    await qc.invalidateQueries({ queryKey: ['quote'] });
  };
  const save = useMutation({
    mutationFn: (patch: PricelistUpdate) => api.updatePricelist(id, patch),
    onSuccess: refresh,
  });
  const [from, setFrom] = useState('');
  const [till, setTill] = useState('');
  const addP = useMutation({
    mutationFn: () => api.addPeriod(id, { from, till }),
    onSuccess: async () => {
      setFrom('');
      setTill('');
      await refresh();
    },
  });
  const delP = useMutation({ mutationFn: (pid: number) => api.deletePeriod(id, pid), onSuccess: refresh });
  const patch = useMemo(() => (q.data && d ? diff(q.data, d) : null), [q.data, d]);
  const dirty = !!(q.data && d && JSON.stringify(d) !== JSON.stringify(toDraft(q.data)));
  const message = errText(save.error) ?? errText(addP.error) ?? errText(delP.error);

  if (q.isPending || !d) return <div className="container"><div className="card state">Načítám…</div></div>;
  if (q.isError || !q.data) {
    return (
      <div className="container">
        <div className="card state" role="alert">
          Ceník se nepodařilo načíst.
        </div>
      </div>
    );
  }
  const p = q.data;
  const setCol = (prod: PricelistProduct, i: number, v: string) => {
    const cols = { ...d.columns, [prod]: d.columns[prod].map((x, j) => (j === i ? v : x)) };
    setD({ ...d, columns: cols });
  };
  /** Fill days i+1…31 with a linear step from day i — the way a list is usually typed. */
  const fillDown = (prod: PricelistProduct, i: number) => {
    const cur = Number(d.columns[prod][i]);
    const prev = i > 0 ? Number(d.columns[prod][i - 1]) : 0;
    const step = i > 0 ? cur - prev : cur;
    if (!Number.isFinite(step)) return;
    const arr = [...d.columns[prod]];
    for (let j = i + 1; j < PRICELIST_DAYS - 1; j += 1) arr[j] = String(Math.max(0, cur + step * (j - i)));
    setD({ ...d, columns: { ...d.columns, [prod]: arr } });
  };

  return (
    <div className="container">
      <div className="pagehead">
        <h1>{p.title}</h1>
        <span className="sub">
          ceník #{p.id}
          {p.status !== 'ok' ? ' · vyřazený' : ''}
          {p.reservations ? ` · ${CZK.format(p.reservations)} rezervací` : ''}
        </span>
        <div className="acts">
          <Link to="/nastaveni/ceniky" className="btn sm">
            ← Ceníky
          </Link>
        </div>
      </div>

      <form
        className="card detailcard"
        onSubmit={(e) => {
          e.preventDefault();
          if (canWrite && patch && !save.isPending) save.mutate(patch);
        }}
      >
        <fieldset disabled={!canWrite} style={{ border: 0, padding: 0, margin: 0 }}>
          <h2>Ceník</h2>
          <div className="efgrid">
            <label className="ef wide2">
              Název
              <input value={d.title} maxLength={255} required onChange={(e) => setD({ ...d, title: e.target.value })} />
            </label>
            <label className="ef wide2">
              Promo kódy (oddělte čárkou; kód vybere tento ceník)
              <input value={d.promo} onChange={(e) => setD({ ...d, promo: e.target.value })} placeholder="např. LETO10, FB10" />
            </label>
            <label className="ef">
              <span className="chk" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                <input type="checkbox" checked={d.dynamic} onChange={(e) => setD({ ...d, dynamic: e.target.checked })} /> Dynamický (obsazenost zvedá cenu)
              </span>
            </label>
            <label className="ef">
              Kapacita venku (pro dynamiku)
              <input type="number" min={0} value={d.capacityOutside} onChange={(e) => setD({ ...d, capacityOutside: e.target.value })} placeholder="prázdné = 380" />
            </label>
            <label className="ef">
              Kapacita garáž (pro dynamiku)
              <input type="number" min={0} value={d.capacityGarage} onChange={(e) => setD({ ...d, capacityGarage: e.target.value })} placeholder="prázdné = 70" />
            </label>
            <label className="ef">
              Stav
              <select value={d.status} onChange={(e) => setD({ ...d, status: e.target.value as 'ok' | 'deleted' })}>
                <option value="ok">živý</option>
                <option value="deleted">vyřazený (staré rezervace ho drží)</option>
              </select>
            </label>
            <label className="ef wide">
              Poznámka (jen pro správce)
              <textarea rows={2} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} />
            </label>
          </div>

          <h3 className="efh">Ceny podle délky pobytu (Kč celkem za pobyt)</h3>
          <p className="settings-note">
            Řádek 32 je <b>každý další den</b> nad 31: cena za 40 dní = den 31 + 9 × řádek 32. Venku s nulou bere cenu Garáž (tak to dělá i web).
            Tlačítko ↓ doplní dny pod sebou stejným krokem, jaký je mezi tímto a předchozím dnem.
          </p>
          <div className="tablewrap sestavawrap">
            <table className="mini settings cenik">
              <thead>
                <tr>
                  <th>Den</th>
                  {PRICELIST_PRODUCTS.map((prod) => (
                    <th key={prod} className="num" colSpan={2}>
                      {PRICELIST_PRODUCT_LABEL[prod]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: PRICELIST_DAYS }, (_, i) => (
                  <tr key={i} className={i === PRICELIST_DAYS - 1 ? 'extra' : undefined}>
                    <td>{i === PRICELIST_DAYS - 1 ? 'každý další den' : `${i + 1}`}</td>
                    {PRICELIST_PRODUCTS.map((prod) => (
                      <Fragment key={prod}>
                        <td className="num">
                          <input
                            type="number"
                            min={0}
                            max={65535}
                            step={1}
                            value={d.columns[prod][i]}
                            onChange={(e) => setCol(prod, i, e.target.value)}
                            aria-label={`${PRICELIST_PRODUCT_LABEL[prod]} — den ${i + 1}`}
                          />
                        </td>
                        <td className="act">
                          {i < PRICELIST_DAYS - 2 && canWrite && (
                            <button type="button" className="btn sm" title="Doplnit dny pod sebou stejným krokem" onClick={() => fillDown(prod, i)}>
                              ↓
                            </button>
                          )}
                        </td>
                      </Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset>
        <div className="efactions">
          {canWrite ? (
            <button type="submit" className="btn primary" disabled={!dirty || !patch || save.isPending}>
              {save.isPending ? 'Ukládám…' : 'Uložit ceník'}
            </button>
          ) : (
            <span className="empty">Ceníky mění majitel.</span>
          )}
          {dirty && !patch && <span className="arrive-error">Některá cena nebo kapacita není celé číslo 0–65 535.</span>}
          {save.isSuccess && !dirty && <span className="arrived-mark">✔ Uloženo</span>}
          {message && (
            <span className="arrive-error" role="alert">
              {message}
            </span>
          )}
        </div>
      </form>

      <div className="card detailcard" style={{ marginTop: 14 }}>
        <h2>Platnost</h2>
        <p className="settings-note">
          Bez období platí ceník vždy. S obdobími platí jen pro příjezd v některém z nich; mimo ně web počítá dvojnásobek (jako by nebylo místo).
        </p>
        {p.periods.length === 0 ? (
          <div className="empty">Platí vždy.</div>
        ) : (
          <table className="mini settings">
            <tbody>
              {p.periods.map((w) => (
                <tr key={w.id}>
                  <td>
                    <b>{czDay(w.from)}</b> – <b>{czDay(w.till)}</b>
                  </td>
                  <td className="act">
                    {canWrite && (
                      <button type="button" className="btn sm" disabled={delP.isPending} onClick={() => delP.mutate(w.id)}>
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canWrite && (
          <form
            className="editform"
            style={{ marginTop: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (from && till && from <= till && !addP.isPending) addP.mutate();
            }}
          >
            <div className="efgrid">
              <label className="ef">
                Od
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
              </label>
              <label className="ef">
                Do
                <input type="date" value={till} onChange={(e) => setTill(e.target.value)} required />
              </label>
            </div>
            <div className="efactions">
              <button type="submit" className="btn" disabled={!from || !till || from > till || addP.isPending}>
                Přidat období
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
